// src/services/ConcurrencyService.js
// Detects concurrent edits to the same object by multiple users (or multiple tabs).
// Uses optimistic locking: each object type+id has a version record (modifiedAt, modifiedBy).
// Services call recordSave() when saving; components call checkConflict() before saving.

import storage from './StorageAdapter';
import sessionService from './SessionService';

const VERSIONS_KEY = 'rms_concurrency_versions';
const LOCKS_KEY = 'rms_concurrency_locks';
// Locks expire after 30 minutes of inactivity
const LOCK_TTL_MS = 30 * 60 * 1000;

class ConcurrencyService {
  _getVersions() {
    return storage.getJSON(VERSIONS_KEY, {});
  }

  async _setVersions(versions) {
    await storage.setJSON(VERSIONS_KEY, versions);
  }

  _getLocks() {
    return storage.getJSON(LOCKS_KEY, {});
  }

  async _setLocks(locks) {
    await storage.setJSON(LOCKS_KEY, locks);
  }

  _lockKey(objectType, objectId) {
    return `${objectType}:${objectId}`;
  }

  _getCurrentUser() {
    const session = sessionService.getSession();
    return session ? session.username : 'unknown';
  }

  /**
   * Record that an object was saved. Call this inside service update methods.
   * @param {string} objectType - e.g. 'bwra_risk', 'georisk_country', 'georisk_index'
   * @param {string} objectId - unique ID of the object
   * @param {string} [username] - who saved; defaults to current session user
   * @returns {{ modifiedAt: number, modifiedBy: string }} the version that was recorded
   */
  async recordSave(objectType, objectId, username) {
    const versions = this._getVersions();
    const key = this._lockKey(objectType, objectId);
    const modifiedAt = Date.now();
    const modifiedBy = username || this._getCurrentUser();
    versions[key] = { modifiedAt, modifiedBy };
    await this._setVersions(versions);
    // Release edit lock when saved (editing is done)
    await this.releaseEditLock(objectType, objectId);
    return { modifiedAt, modifiedBy };
  }

  /**
   * Get the current version record for an object.
   * @param {string} objectType
   * @param {string} objectId
   * @returns {{ modifiedAt: number, modifiedBy: string } | null}
   */
  getObjectVersion(objectType, objectId) {
    const versions = this._getVersions();
    return versions[this._lockKey(objectType, objectId)] || null;
  }

  /**
   * Check whether an object was modified since the caller loaded it.
   * @param {string} objectType
   * @param {string} objectId
   * @param {number | null} baseModifiedAt - timestamp captured when editing started; null if object is new
   * @returns {{ hasConflict: false } | { hasConflict: true, current: { modifiedAt: number, modifiedBy: string } }}
   */
  checkConflict(objectType, objectId, baseModifiedAt) {
    // New objects (no baseModifiedAt) can never have a conflict
    if (baseModifiedAt == null) {
      return { hasConflict: false };
    }
    const current = this.getObjectVersion(objectType, objectId);
    if (!current) {
      return { hasConflict: false };
    }
    if (current.modifiedAt !== baseModifiedAt) {
      return { hasConflict: true, current };
    }
    return { hasConflict: false };
  }

  /**
   * Signal that a user started editing an object (optional "presence" indicator).
   * The lock automatically expires after LOCK_TTL_MS milliseconds.
   * @param {string} objectType
   * @param {string} objectId
   * @param {string} [username]
   */
  async acquireEditLock(objectType, objectId, username) {
    const locks = this._getLocks();
    const key = this._lockKey(objectType, objectId);
    locks[key] = {
      lockedBy: username || this._getCurrentUser(),
      lockedAt: Date.now(),
    };
    await this._setLocks(locks);
  }

  /**
   * Release an edit lock.
   * @param {string} objectType
   * @param {string} objectId
   */
  async releaseEditLock(objectType, objectId) {
    const locks = this._getLocks();
    const key = this._lockKey(objectType, objectId);
    delete locks[key];
    await this._setLocks(locks);
  }

  /**
   * Get active editor for an object (null if no one or lock expired).
   * @param {string} objectType
   * @param {string} objectId
   * @returns {{ lockedBy: string, lockedAt: number } | null}
   */
  getActiveLock(objectType, objectId) {
    const locks = this._getLocks();
    const lock = locks[this._lockKey(objectType, objectId)];
    if (!lock) return null;
    // Expire stale locks
    if (Date.now() - lock.lockedAt > LOCK_TTL_MS) {
      this.releaseEditLock(objectType, objectId);
      return null;
    }
    return lock;
  }

  /**
   * Clean up all expired locks. Call periodically or on app init.
   */
  async cleanExpiredLocks() {
    const locks = this._getLocks();
    const now = Date.now();
    let changed = false;
    for (const key of Object.keys(locks)) {
      if (now - locks[key].lockedAt > LOCK_TTL_MS) {
        delete locks[key];
        changed = true;
      }
    }
    if (changed) await this._setLocks(locks);
  }
}

const concurrencyService = new ConcurrencyService();
export default concurrencyService;
